import { type Event } from "@shared/schema";

/**
 * Filters out past events based on eventDate
 * 
 * @param events - Array of events to filter
 * @returns Array of upcoming events only
 */
export function filterUpcomingEvents(events: Event[]): Event[] {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today

  return events.filter(event => {
    if (!event.eventDate) return true; // Keep events without date
    const eventDate = new Date(event.eventDate);
    return eventDate >= now;
  });
}

/**
 * Filters events to only include those within the next 7 days (including today)
 * 
 * @param events - Array of events to filter
 * @returns Array of events happening within 7 days from today
 * 
 * @remarks
 * Uses local date comparison to avoid timezone issues. Events are compared
 * by calendar date only (year, month, day) without time components.
 */
export function filterEventsWithin7Days(events: Event[]): Event[] {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today in local time
  
  const sevenDaysFromNow = new Date(now);
  sevenDaysFromNow.setDate(now.getDate() + 6); // Day 7 is 6 days from today (inclusive)
  sevenDaysFromNow.setHours(23, 59, 59, 999); // End of day 7

  return events.filter(event => {
    if (!event.eventDate) return false; // Exclude events without date
    
    // Parse eventDate as local calendar date to avoid timezone shifts
    let year: number, month: number, day: number;
    
    if (event.eventDate instanceof Date) {
      // For Date objects, use local date components directly
      year = event.eventDate.getFullYear();
      month = event.eventDate.getMonth() + 1; // getMonth() is 0-based
      day = event.eventDate.getDate();
    } else {
      // For ISO strings, extract YYYY-MM-DD portion
      const eventDateStr = String(event.eventDate).split('T')[0];
      [year, month, day] = eventDateStr.split('-').map(Number);
    }
    
    if (!year || !month || !day) return false;
    
    // Create local date using year, month, day components (avoids timezone parsing)
    const eventDate = new Date(year, month - 1, day); // month is 1-based, Date constructor needs 0-based
    eventDate.setHours(0, 0, 0, 0); // Normalize to start of day
    
    return eventDate >= now && eventDate <= sevenDaysFromNow;
  });
}

/**
 * Picks a random event from the array, avoiding the last shown event if possible
 * 
 * @param events - Array of events to choose from
 * @param excludeId - ID of the last event shown (to avoid immediate repeats)
 * @returns A random event, or null if no events available
 * 
 * @example
 * const randomEvent = pickRandomEvent(upcomingEvents, lastEventId);
 * 
 * @remarks
 * - Returns null if events array is empty
 * - If only 1 event exists, returns it (ignores excludeId)
 * - If excludeId matches all events, re-rolls without exclusion
 * - Uses uniform distribution for random selection
 */
export function pickRandomEvent(
  events: Event[],
  excludeId?: string
): Event | null {
  if (events.length === 0) {
    return null;
  }

  // If only one event, return it
  if (events.length === 1) {
    return events[0];
  }

  // Try to pick a different event than the last one
  if (excludeId) {
    const otherEvents = events.filter(e => e.id !== excludeId);
    
    // If we have other events, pick from them
    if (otherEvents.length > 0) {
      const randomIndex = Math.floor(Math.random() * otherEvents.length);
      return otherEvents[randomIndex];
    }
  }

  // Fall back to picking any event (uniform random)
  const randomIndex = Math.floor(Math.random() * events.length);
  return events[randomIndex];
}

/**
 * To adjust repeat-avoidance behavior:
 * - Increase N to avoid last N events (currently N=1)
 * - Change the filter logic in pickRandomEvent to track multiple excludeIds
 * - For stricter randomness, implement Fisher-Yates shuffle instead
 */
